import DeleteIcon from "@mui/icons-material/Delete";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import { TableCellType } from "components/LazyTable";
import ResponsiveIframe from "components/ResponsiveIframe";
import SnackbarMessage from "components/SnackbarMessage";
import SortableTable from "components/SortableTable";
import { FORM_ERROR } from "final-form";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useCallback, useMemo, useState } from "react";
import { DropResult } from "react-beautiful-dnd";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  Attribute,
  ListRequestOfAttributeFilter,
  ListResponseOfSkuAttributeByType,
  SkuAttributeExtended,
  SkuAttributeSequenceRequest,
} from "services/KnowfullyAdminClient";
import {
  AttributeCodeConstants,
  AttributeDisplayTypes,
  Severity,
  SnackbarMessageProps,
} from "types/common-types";
import {
  AttributeWithId,
  SkuAttributeByTypeWithId,
  SkuFormProps,
} from "types/sku-related-types";
import translateError from "utils/translateError";
import {
  buildAttributeDataCells,
  flattenLocationMetaValues,
  flattenSponsorMetaValues,
  getAttributeCodeConstant,
  hasLocationAttributeGroup,
  useLocationLookups,
} from "./attributeColumnsUtils";
import SkuAttributeForm from "./SkuAttributeForm";
import SkuAttributeUpdateForm from "./SkuAttributeUpdateForm";
import {
  attributeTypeConfigs,
  getMetaValueComponent,
  metaValueLabelKeys,
} from "pages/AttributesPage/CustomAttributeComponents";

const SkuAttributes = (props: SkuFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { skuService } = useGlobalContext();
  const [deleting, setDeleting] = useState<Attribute | null>(null);
  const [updating, setUpdating] = useState<SkuAttributeExtended>();
  const [previewing, setPreviewing] = useState<Attribute | null>(null);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [createOpen, createOpenActions] = useBoolean(false);
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const [previewOpen, previewOpenActions] = useBoolean(false);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const DeleteButton = ({ value }: { value: Attribute }) => {
    return (
      <Box textAlign="center">
        <IconButton
          size="small"
          onClick={() => {
            setDeleting(value);
            deleteOpenActions.setTrue();
          }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  };

  const PreviewButton = ({ value }: { value: Attribute }) => {
    return (
      <Box textAlign="center">
        <IconButton
          size="small"
          onClick={() => {
            setPreviewing(value);
            previewOpenActions.setTrue();
          }}
        >
          <VisibilityIcon />
        </IconButton>
      </Box>
    );
  };

  const EditButton = ({ value }: { value: SkuAttributeExtended }) => {
    return (
      <Box textAlign="center">
        <IconButton size="small" onClick={() => setUpdating(value)}>
          <EditIcon />
        </IconButton>
      </Box>
    );
  };

  const handleDelete = useCallback(async () => {
    const id = deleting?.id;
    setDeleting(null);

    try {
      if (id) {
        await registerPromise(skuService.deleteSkuAttribute(id));
        queryClient.invalidateQueries(["skuAttributes", props.sku.id!]);
        queryClient.invalidateQueries(["skuAttributesByType", props.sku.id!]);

        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "skuAttributeDeleted" }),
            Severity.Warning
          )
        );
        props.onSkuUpdated && props.onSkuUpdated(props.sku.id!);
        deleteOpenActions.setFalse();
      }
    } catch (error) {
      console.log(error);
      return { [FORM_ERROR]: translateError(error, t) };
    }
  }, [
    deleteOpenActions,
    deleting?.id,
    props,
    queryClient,
    registerPromise,
    skuService,
    t,
  ]);

  const request = ListRequestOfAttributeFilter.fromJS({
    sortField: "createdOn",
    sortDescending: true,
  });

  const { isLoading, data } = useStrictQuery<ListResponseOfSkuAttributeByType>(
    ["skuAttributesByType", props.sku.id, request],
    () => skuService.getSkuAttributesByType(props.sku.id!, request)
  );

  const skuAttributes = useMemo(
    () => (data?.data ?? []) as SkuAttributeByTypeWithId[],
    [data?.data]
  );

  const locationLookups = useLocationLookups(
    hasLocationAttributeGroup(skuAttributes)
  );

  const buildCells = (
    allowDates: boolean,
    codeConstant?: string
  ): Array<TableCellType> => [
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: DragHandleIcon,
      label: "",
      noSort: true,
    } as TableCellType,
    ...buildAttributeDataCells({
      codeConstant,
      allowDates,
      defaultAccessor: "value",
      t,
    }),
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: PreviewButton,
      label: "",
      noSort: true,
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: EditButton,
      label: "",
      noSort: true,
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: DeleteButton,
      label: "",
      noSort: true,
    },
  ];

  const onAttributeCreated = () => {
    props.onSkuUpdated && props.onSkuUpdated(props.sku.id!);
    createOpenActions.setFalse();

    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "skuAttributeCreated" }))
    );
  };

  const onDragEndHandler = async (result: DropResult) => {
    try {
      if (!result.destination) {
        return;
      }

      let attributes: SkuAttributeExtended[] = [];
      skuAttributes.forEach((x) => {
        x.attributes?.forEach((attr) => attributes.push(attr));
      });

      var targetSkuAttribute = attributes.find(
        (x) => x.id === result.draggableId
      );

      if (!targetSkuAttribute) {
        return;
      }

      await skuService.updateSkuAttributeSequence(
        props.sku.id!,
        new SkuAttributeSequenceRequest({
          skuAttributeId: targetSkuAttribute.id!,
          sequence: result.destination?.index,
          attributeTypeId: targetSkuAttribute.attributeTypeId!,
        })
      );
      queryClient.invalidateQueries(["skuAttributesByType", props.sku.id!]);
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  const previewAttributeConfig = attributeTypeConfigs.find(
    (c) =>
      c.codeConstant ===
      previewing?.organizationAttributeType?.attributeType?.codeConstant?.toLowerCase()
  );

  return (
    <Container>
      <Box my={1} textAlign="right">
        <Button
          variant="outlined"
          type="submit"
          color="primary"
          onClick={createOpenActions.setTrue}
        >
          <T id="skuAttributeCreateButtonLabel" />
        </Button>
      </Box>

      {isLoading ? (
        <CircularProgress size={24} />
      ) : (
        skuAttributes
          .sort((a, b) => (a?.type! > b?.type! ? 1 : -1))
          .filter(
            (a) =>
              a.attributeDisplayTypeId ===
              AttributeDisplayTypes.AttributeSection
          )
          .map((x, index) => {
            const codeConstant = getAttributeCodeConstant(x.attributes);
            const attributes =
              codeConstant === AttributeCodeConstants.Locations
                ? flattenLocationMetaValues(
                    x.attributes as AttributeWithId[],
                    locationLookups
                  )
                : codeConstant === AttributeCodeConstants.Sponsors
                ? flattenSponsorMetaValues(
                    x.attributes as AttributeWithId[],
                    t
                  )
                : (x.attributes as AttributeWithId[]);
            return (
              <div key={index}>
                <SortableTable
                  {...{
                    rows: attributes,
                    loading: isLoading,
                    dense: true,
                    allowDragAndDrop: true,
                    title: x.type,
                    cells: buildCells(!!x.allowDates, codeConstant),
                    defaultOrderBy: "createdOn",
                    onDragAndDropEnd: onDragEndHandler,
                    rowsPerPage,
                    setRowsPerPage,
                  }}
                />
              </div>
            );
          })
      )}

      <DeleteEntityDialog
        dialogTitleKey="skuAttributeDeleteTitle"
        contentKey="skuAttributeDeleteQuestion"
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />

      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <SkuAttributeForm
          {...props}
          setSnackbarMessage={setSnackbarMessage}
          skuAttributes={skuAttributes}
          onAttributeCreated={onAttributeCreated}
          onCancel={createOpenActions.setFalse}
        />
      </Dialog>

      <Dialog open={!!updating} disableEnforceFocus maxWidth="sm" fullWidth>
        <SkuAttributeUpdateForm
          {...props}
          setSnackbarMessage={setSnackbarMessage}
          skuAttribute={updating}
          skuAttributes={skuAttributes}
          close={() => setUpdating(undefined)}
        />
      </Dialog>

      <Dialog open={previewOpen} disableEnforceFocus maxWidth="md" fullWidth>
        <DialogContent>
          <Paper
            style={{
              height: "100%",
              padding: "15px",
              backgroundColor: "#fefdfa",
            }}
          >
            {previewAttributeConfig ? (
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: "bold" }}>
                      {t({ id: "nameLabel" })}
                    </TableCell>
                    <TableCell>{previewing?.value}</TableCell>
                  </TableRow>
                  {previewAttributeConfig.metaValues.map((metaValueName) => {
                    const config = getMetaValueComponent(metaValueName);
                    if (!config) return null;
                    const hasValue = previewing?.attributeMetaValues?.find(
                      (m) => m.name === metaValueName,
                    )?.value;
                    if (!hasValue) return null;
                    const { Preview } = config;
                    const labelKey = metaValueLabelKeys[metaValueName] ?? metaValueName;
                    return (
                      <TableRow key={metaValueName}>
                        <TableCell component="th" sx={{ fontWeight: "bold" }}>
                          {t({ id: labelKey })}
                        </TableCell>
                        <TableCell>
                          <Preview
                            metaValueName={metaValueName}
                            displayText={previewing?.displayText}
                            attributeMetaValues={previewing?.attributeMetaValues}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <ResponsiveIframe
                srcDoc={previewing?.value!}
                title="previewAttribute"
                containerStyles={{ minHeight: "200px" }}
              />
            )}
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button
            id={"cancelPreview"}
            onClick={() => {
              previewOpenActions.setFalse();
            }}
          >
            {t({ id: "closeButton" })}
          </Button>
        </DialogActions>
      </Dialog>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default withErrorBoundary(SkuAttributes);
